// WebView - Simplified SDK in TypeScript

// Define types
interface InitParams {
  [key: string]: any;
  tgWebAppPlatform?: string;
}

// Define WebViewEventCallback type and BackButtonInterface before using them
type WebViewEventCallback = (eventType: string, ...args: any[]) => void;

// BackButton interface definition
interface BackButtonInterface {
  isVisible: boolean;
  onClick(callback: WebViewEventCallback): BackButtonInterface;
  offClick(callback: WebViewEventCallback): BackButtonInterface;
  show(): BackButtonInterface;
  hide(): BackButtonInterface;
}

// Bot API 8.0 安全区，单位为 CSS px。
export interface SafeAreaInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export type FullscreenChangedCallback = (data: { isFullscreen: boolean }) => void;
// error: UNSUPPORTED | ALREADY_FULLSCREEN
export type FullscreenFailedCallback = (data: { error?: string }) => void;
// unsupported：客户端 < 6.9 或已有待决请求；null：等原生弹窗超时无应答
export type WriteAccessStatus = 'allowed' | 'cancelled' | 'unsupported';

interface TelegramWebView {
  initParams: InitParams;
  initData: string;
  platform: string;
  expand: () => void;
  enableClosingConfirmation: () => void;
  disableVerticalSwipes: () => void;
  requestFullscreen: () => void;
  exitFullscreen: () => void;
  requestSafeArea: () => void;
  requestContentSafeArea: () => void;
  isFullscreen: () => boolean;
  safeAreaInset: () => SafeAreaInset;
  contentSafeAreaInset: () => SafeAreaInset;
  // 客户端 Bot API 版本是否 >= ver（如 '8.0'）。启动参数丢失时为 '6.0'，见下方 sessionStorage 回填
  isVersionAtLeast: (ver: string) => boolean;
  onFullscreenChanged: (callback: FullscreenChangedCallback) => void;
  offFullscreenChanged: (callback: FullscreenChangedCallback) => void;
  onFullscreenFailed: (callback: FullscreenFailedCallback) => void;
  offFullscreenFailed: (callback: FullscreenFailedCallback) => void;
  BackButton: BackButtonInterface;
  setBackgroundColor: (color: string) => void
  downloadFile: (params: any) => void
  openLink: (url: string, options: any) => void
  openTelegramLink: (url: string, options: any) => void
  readTextFromClipboard: () => Promise<string | null>
  // Bot API 6.9+。请求「允许 bot 给我发消息」的原生弹窗；已允许时客户端直接回 allowed。
  // 不支持 → 'unsupported'（调用方兜底跳 bot 会话）；等待超时无应答 → null
  requestWriteAccess: () => Promise<WriteAccessStatus | null>
}

// Create and export the WebView module
const createTelegramWebView = (): TelegramWebView => {
  let webAppVersion = '6.0';
  // Parse URL parameters to get initData
  let locationHash = '';
  if (typeof window !== 'undefined') {
    if (!window.Telegram) {
      window.Telegram = {};
    }
    window.Telegram.WebView = {
      onEvent: onEvent,
      offEvent: offEvent,
      postEvent: postEvent,
      receiveEvent: receiveEvent,
      callEventCallbacks: callEventCallbacks
    };

    window.Telegram.Utils = {
      urlSafeDecode: urlSafeDecode,
      urlParseQueryString: urlParseQueryString,
      urlParseHashParams: urlParseHashParams,
    };

    // App backward compatibility
    window.TelegramGameProxy = {
      receiveEvent: receiveEvent
    };
  }

  try {
    locationHash = location.hash.toString();
  } catch (e) {
    // Ignore error
  }

  function receiveEvent(eventType: string, eventData: any) {
    console.log('[Telegram.WebView1] < receiveEvent', eventType, eventData);
    callEventCallbacks(eventType, (callback) => {
      callback(eventType, eventData);
    });
  }

  // 请求回调登记表 + 随机 req_id 生成，移植自官方 telegram-web-app.js
  const webAppCallbacks: Record<string, { callback?: (...args: any[]) => void }> = {};
  function generateCallbackId(len: number): string {
    let tries = 100;
    while (--tries) {
      let id = '';
      const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      for (let i = 0; i < len; i++) {
        id += chars[Math.floor(Math.random() * chars.length)];
      }
      if (!webAppCallbacks[id]) {
        webAppCallbacks[id] = {};
        return id;
      }
    }
    throw Error('WebAppCallbackIdGenerateFailed');
  }

  // write_access_requested 事件不带 req_id，同一时刻只会有一个待决请求，用单个 resolver 承接
  let pendingWriteAccess: ((status: WriteAccessStatus | null) => void) | null = null;
  function onWriteAccessRequested(eventType: string, eventData: any) {
    const resolve = pendingWriteAccess;
    pendingWriteAccess = null;
    resolve?.(eventData?.status === 'allowed' ? 'allowed' : 'cancelled');
  }

  // 剪贴板读取应答：Telegram 通过 clipboard_text_received 事件回带发起时的 req_id
  function onClipboardTextReceived(eventType: string, eventData: any) {
    if (eventData.req_id && webAppCallbacks[eventData.req_id]) {
      const requestData = webAppCallbacks[eventData.req_id];
      delete webAppCallbacks[eventData.req_id];
      let data = null;
      if (typeof eventData.data !== 'undefined') {
        data = eventData.data;
      }
      if (requestData.callback) {
        requestData.callback(data);
      }
    }
  }

  var webAppDownloadFileRequested: any = false;
  function onFileDownloadRequested(eventType: string, eventData: any) {
    if (webAppDownloadFileRequested) {
      var requestData = webAppDownloadFileRequested;
      webAppDownloadFileRequested = false;
      var isDownloading = eventData.status == 'downloading';
      if (requestData.callback) {
        requestData.callback(isDownloading);
      }
      receiveWebViewEvent('fileDownloadRequested', {
        status: isDownloading ? 'downloading' : 'cancelled'
      });
    }
  }

  // ===== Bot API 8.0：全屏 & 安全区 =====
  // 事件解析对齐官方 telegram-web-app.js 的 setSafeAreaInset / setContentSafeAreaInset：
  //   --tg-safe-area-inset-*          设备自身的安全区（刘海、状态栏、home indicator）
  //   --tg-content-safe-area-inset-*  Telegram 画在 WebView "之上"的自己的 UI 所占位置
  //                                   （全屏时右上角那个 ✕/⋯ 胶囊），CSS 的 env() 看不见它
  // 这两个原始值只为真机核对用；页面真正消费的是 applyTopInset 合成的 --tg-top-safe
  // （= safe.top + content.top，全屏时 content.top 有下限），见 globals.css 的 --top。
  //
  // 与官方的有意差异：
  // - 官方初始化时就把变量写成 0px，这里只在真正收到事件后才写；
  // - --tg-top-safe 只在全屏状态下存在，非全屏一律移除。
  // 两条都是为了让 CSS 的 var(--tg-top-safe, env(safe-area-inset-top)) 在
  // < 8.0 老客户端、Desktop / Web、以及退出全屏后都回退到 env()，与改动前的行为完全一致。
  const zeroInset = (): SafeAreaInset => ({ top: 0, bottom: 0, left: 0, right: 0 });
  let safeAreaState: SafeAreaInset = zeroInset();
  let contentSafeAreaState: SafeAreaInset = zeroInset();
  let isFullscreenState = false;

  function setCssProperty(name: string, value: string) {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (root && root.style && root.style.setProperty) {
      root.style.setProperty('--tg-' + name, value);
    }
  }

  // 官方对每个字段单独判 undefined 后才覆盖，缺字段时保留旧值，这里照抄该语义
  // 全屏时 Telegram 那颗 ✕/⋯ 胶囊是画在 WebView 之上的浮层。部分客户端会把
  // content_safe_area_changed 的 top 报成 0（等于"我没挡住你"），但胶囊照样压在顶栏上。
  // 这里给一个下限兜底：胶囊本体约 30px，加上它与状态栏之间的间距，46px 可以让顶栏完全露出。
  // 客户端如实上报时（top >= 46）该下限不生效，用客户端的值。
  const FULLSCREEN_MIN_CONTENT_TOP = 46;

  // 顶部避让的最终值在 JS 里算，而不是放到 CSS 的 max() 里：
  // CSS max() 需要 Chrome 79，本项目要兼容 Chrome 77+（见 AGENTS.md）。
  // 原始值仍按官方命名单独暴露，方便真机上直接读数、核对下面这个下限是否合适。
  function applyTopInset() {
    if (typeof document === 'undefined' || !document.documentElement) return;
    const root = document.documentElement;
    root.classList.toggle('tg-fullscreen', isFullscreenState);
    if (!isFullscreenState) {
      // 退出全屏（用户关开关 / 点胶囊收起）时 WebView 已经回到 Telegram 标题栏之下，
      // 此刻手里的 inset 还是全屏期间的值，若照常写出去顶部会多出一整条空带，
      // 直到客户端重新回报为止（个别客户端不会再回报）。非全屏不发布，CSS 回退到 env()。
      root.style.removeProperty('--tg-top-safe');
      return;
    }
    const contentTop = Math.max(contentSafeAreaState.top, FULLSCREEN_MIN_CONTENT_TOP);
    const topSafe = safeAreaState.top + contentTop;
    setCssProperty('top-safe', topSafe + 'px');
    process.env.NEXT_PUBLIC_RM_CONSOLE != 'true' && console.log('[TG] --tg-top-safe=%spx (safe.top=%s, content.top=%s)',
      topSafe, safeAreaState.top, contentSafeAreaState.top);
  }

  function assignInset(target: SafeAreaInset, data: any) {
    (['top', 'bottom', 'left', 'right'] as const).forEach((k) => {
      if (typeof data?.[k] !== 'undefined') {
        const n = typeof data[k] === 'number' ? data[k] : parseFloat(data[k]);
        target[k] = isFinite(n) && n > 0 ? n : 0;
      }
    });
  }

  function onSafeAreaChanged(eventType: string, eventData: any) {
    if (!eventData) return;
    assignInset(safeAreaState, eventData);
    setCssProperty('safe-area-inset-top', safeAreaState.top + 'px');
    setCssProperty('safe-area-inset-bottom', safeAreaState.bottom + 'px');
    setCssProperty('safe-area-inset-left', safeAreaState.left + 'px');
    setCssProperty('safe-area-inset-right', safeAreaState.right + 'px');
    applyTopInset();
    receiveWebViewEvent('safeAreaChanged', { ...safeAreaState });
  }

  function onContentSafeAreaChanged(eventType: string, eventData: any) {
    if (!eventData) return;
    assignInset(contentSafeAreaState, eventData);
    setCssProperty('content-safe-area-inset-top', contentSafeAreaState.top + 'px');
    setCssProperty('content-safe-area-inset-bottom', contentSafeAreaState.bottom + 'px');
    setCssProperty('content-safe-area-inset-left', contentSafeAreaState.left + 'px');
    setCssProperty('content-safe-area-inset-right', contentSafeAreaState.right + 'px');
    applyTopInset();
    receiveWebViewEvent('contentSafeAreaChanged', { ...contentSafeAreaState });
  }

  // 这里必须重新请求 inset —— 这是本项目与官方 telegram-web-app.js 的关键差异：
  // 官方全局只在初始化时请求一次，因为它假设小程序是"启动即全屏"；
  // 我们是在 authContext 里【运行时】才调 requestFullscreen，先发出的那次请求
  // 拿回来的是进全屏【之前】的值（content.top = 0），不重拉就会漏掉这一拍，
  // 表现就是顶栏被 ✕/⋯ 胶囊压住。
  function onFullscreenChanged(eventType: string, eventData: any) {
    isFullscreenState = !!eventData?.is_fullscreen;
    applyTopInset();
    postEvent('web_app_request_safe_area');
    postEvent('web_app_request_content_safe_area');
    receiveWebViewEvent('fullscreenChanged', { isFullscreen: isFullscreenState });
  }

  function onFullscreenFailed(eventType: string, eventData: any) {
    // error: UNSUPPORTED | ALREADY_FULLSCREEN
    console.warn('[Telegram.WebApp] fullscreen_failed:', eventData?.error);
    if (eventData?.error === 'ALREADY_FULLSCREEN' && !isFullscreenState) {
      isFullscreenState = true;
      applyTopInset();
      postEvent('web_app_request_safe_area');
      postEvent('web_app_request_content_safe_area');
    }
    receiveWebViewEvent('fullscreenFailed', { error: eventData?.error });
  }

  let isIframe = false;
  let iFrameStyle: HTMLStyleElement | null = null;

  try {
    isIframe = (typeof window !== 'undefined' && window.parent != null && window !== window.parent);

    if (isIframe) {
      window.addEventListener('message', (event: MessageEvent) => {
        if (event.source !== window.parent) return;

        try {
          const dataParsed = JSON.parse(event.data as string);

          if (!dataParsed || !dataParsed.eventType) {
            return;
          }

          if (dataParsed.eventType === 'set_custom_style') {
            if (event.origin === 'https://web.telegram.org' && iFrameStyle) {
              iFrameStyle.innerHTML = dataParsed.eventData;
            }
          } else if (dataParsed.eventType === 'reload_iframe') {
            try {
              window.parent.postMessage(JSON.stringify({ eventType: 'iframe_will_reload' }), '*');
            } catch (e) {
              // Ignore error
            }
            location.reload();
          } else {
            receiveEvent(dataParsed.eventType, dataParsed.eventData);
          }
        } catch (e) {
          return;
        }
      });

      iFrameStyle = document.createElement('style');
      document.head.appendChild(iFrameStyle);

      try {
        window.parent.postMessage(JSON.stringify({
          eventType: 'iframe_ready',
          eventData: { reload_supported: true }
        }), '*');
      } catch (e) {
        // Ignore error
      }
    }
  } catch (e) {
    // Ignore error
  }

  const initParams = urlParseHashParams(locationHash);

  // 启动参数（tgWebAppVersion / tgWebAppPlatform / 主题等）只在 Telegram 首次打开的 URL hash 里，
  // 站内路由一跳 hash 就没了，此时「刷新页面」会解析成 platform=unknown、version=6.0：
  // authContext 据此跳过全屏与安全区请求，--tg-* 变量和 html.tg-fullscreen 全部缺失，
  // 而客户端其实还停留在全屏，顶部布局就会错位。
  // 官方 telegram-web-app.js 的做法是把 initParams 存进 sessionStorage 并在缺失时回填，这里照做。
  // 不回填 tgWebAppData：登录态另有 localStorage('tg-init-data') 兜底，start_param 的一次性消费
  // 也依赖它为空来判断「不是本次启动」，回填会让刷新后重新触发深链跳转。
  const INIT_PARAMS_STORAGE_KEY = '__telegram__initParams';
  try {
    if (typeof sessionStorage !== 'undefined') {
      const stored = JSON.parse(sessionStorage.getItem(INIT_PARAMS_STORAGE_KEY) || 'null');
      if (stored && typeof stored === 'object') {
        for (const k in stored) {
          if (typeof initParams[k] === 'undefined') initParams[k] = stored[k];
        }
      }
      const toStore: Record<string, any> = {};
      for (const k in initParams) {
        if (k.indexOf('tgWebApp') === 0 && k !== 'tgWebAppData') toStore[k] = initParams[k];
      }
      if (Object.keys(toStore).length) {
        sessionStorage.setItem(INIT_PARAMS_STORAGE_KEY, JSON.stringify(toStore));
      }
    }
  } catch (e) {
    // sessionStorage 不可用（隐私模式等）时退回原行为
  }

  // Generate initData string from hash parameters
  let initDataString = '';
  if (initParams.tgWebAppData) {
    initDataString = initParams.tgWebAppData;
  }
  if (initParams.tgWebAppVersion) {
    webAppVersion = initParams.tgWebAppVersion;
  }

  // URL parsing utilities
  function urlSafeDecode(urlencoded: string): string {
    try {
      urlencoded = urlencoded.replace(/\+/g, '%20');
      return decodeURIComponent(urlencoded);
    } catch (e) {
      return urlencoded;
    }
  }

  function urlParseHashParams(locationHash: string): InitParams {
    locationHash = locationHash.replace(/^#/, '');
    const params: InitParams = {};
    if (!locationHash.length) {
      return params;
    }
    if (locationHash.indexOf('=') < 0 && locationHash.indexOf('?') < 0) {
      params._path = urlSafeDecode(locationHash);
      return params;
    }
    const qIndex = locationHash.indexOf('?');
    if (qIndex >= 0) {
      const pathParam = locationHash.substr(0, qIndex);
      params._path = urlSafeDecode(pathParam);
      locationHash = locationHash.substr(qIndex + 1);
    }
    const query_params = urlParseQueryString(locationHash);
    for (const k in query_params) {
      params[k] = query_params[k];
    }
    return params;
  }

  function urlParseQueryString(queryString: string): Record<string, string | null> {
    const params: Record<string, string | null> = {};
    if (!queryString.length) {
      return params;
    }
    const queryStringParams = queryString.split('&');
    let param, paramName, paramValue;
    for (let i = 0; i < queryStringParams.length; i++) {
      param = queryStringParams[i].split('=');
      paramName = urlSafeDecode(param[0]);
      paramValue = param[1] == null ? null : urlSafeDecode(param[1]);
      params[paramName] = paramValue;
    }
    return params;
  }

  // Event posting utility
  function postEvent(eventType: string, eventData?: any): void {
    if (eventData === undefined) {
      eventData = '';
    }

    if ((window as any).TelegramWebviewProxy !== undefined) {
      (window as any).TelegramWebviewProxy.postEvent(eventType, JSON.stringify(eventData));
    }
    else if ((window as any).external && 'notify' in (window as any).external) {
      (window as any).external.notify(JSON.stringify({ eventType, eventData }));
    } else if (isIframe) {
      try {
        var trustedTarget = 'https://web.telegram.org';
        // For now we don't restrict target, for testing purposes
        trustedTarget = '*';
        window.parent.postMessage(JSON.stringify({ eventType: eventType, eventData: eventData }), trustedTarget);
      } catch (e) {
      }
    }
    else {
    }
  }

  function versionCompare(v1: any, v2: any): number {
    if (typeof v1 !== 'string') v1 = '';
    if (typeof v2 !== 'string') v2 = '';
    v1 = v1.replace(/^\s+|\s+$/g, '').split('.');
    v2 = v2.replace(/^\s+|\s+$/g, '').split('.');
    const a = Math.max(v1.length, v2.length);
    for (let i = 0; i < a; i++) {
      const p1 = parseInt(v1[i]) || 0;
      const p2 = parseInt(v2[i]) || 0;
      if (p1 == p2) continue;
      if (p1 > p2) return 1;
      return -1;
    }
    return 0;
  }

  function versionAtLeast(ver: any) {
    return versionCompare(webAppVersion, ver) >= 0;
  }

  // Event handling functions for WebView events
  const eventHandlers: Record<string, WebViewEventCallback[]> = {};

  function onEvent(eventType: string, callback: WebViewEventCallback): void {
    if (eventHandlers[eventType] === undefined) {
      eventHandlers[eventType] = [];
    }
    const index = eventHandlers[eventType].indexOf(callback);
    if (index === -1) {
      eventHandlers[eventType].push(callback);
    }
  }

  function offEvent(eventType: string, callback: WebViewEventCallback): void {
    if (eventHandlers[eventType] === undefined) {
      return;
    }
    const index = eventHandlers[eventType].indexOf(callback);
    if (index === -1) {
      return;
    }
    eventHandlers[eventType].splice(index, 1);
  }

  function callEventCallbacks(eventType: string, func: (callback: WebViewEventCallback) => void): void {
    const curEventHandlers = eventHandlers[eventType];
    if (curEventHandlers === undefined || !curEventHandlers.length) {
      return;
    }
    for (let i = 0; i < curEventHandlers.length; i++) {
      try {
        func(curEventHandlers[i]);
      } catch (e) {
        // Ignore errors in callbacks
      }
    }
  }

  function receiveWebViewEvent(eventType: string, ...args: any[]): void {
    callEventCallbacks('webview:' + eventType, (callback) => {
      callback(eventType, ...args);
    });
  }

  function onWebViewEvent(eventType: string, callback: WebViewEventCallback): void {
    onEvent('webview:' + eventType, callback);
  }

  function offWebViewEvent(eventType: string, callback: WebViewEventCallback): void {
    offEvent('webview:' + eventType, callback);
  }

  // webview 事件回调的签名是 (eventType, data)，对外只暴露 data。
  // 同一个外部回调映射到同一个包装函数，off 时才能找到并解绑。
  const dataOnlyWrappers = new WeakMap<Function, WebViewEventCallback>();
  function dataOnly(callback: (data: any) => void): WebViewEventCallback {
    let wrapped = dataOnlyWrappers.get(callback);
    if (!wrapped) {
      wrapped = (_eventType: string, data: any) => callback(data);
      dataOnlyWrappers.set(callback, wrapped);
    }
    return wrapped;
  }

  // BackButton implementation
  const BackButton = ((): BackButtonInterface => {
    let isVisible = false;
    let curButtonState: string | null = null;

    // Create backButton object with TypeScript interface
    const backButton = {} as BackButtonInterface;

    // Define isVisible property with getter and setter
    Object.defineProperty(backButton, 'isVisible', {
      set: function (val: boolean) { setParams({ is_visible: val }); },
      get: function (): boolean { return isVisible; },
      enumerable: true
    });
    function onBackButtonPressed() {
      receiveWebViewEvent('backButtonClicked');
    }
    onEvent('back_button_pressed', onBackButtonPressed);
    // Helper function to get button parameters
    function buttonParams(): { is_visible: boolean } {
      return { is_visible: isVisible };
    }

    // Helper function to get button state as JSON string
    function buttonState(btn_params?: { is_visible: boolean }): string {
      if (typeof btn_params === 'undefined') {
        btn_params = buttonParams();
      }
      return JSON.stringify(btn_params);
    }

    // Helper function to check if the version supports back button
    function buttonCheckVersion(): boolean {
      if (!versionAtLeast('6.1')) {
        console.warn('[Telegram.WebApp] BackButton is not supported in version ' + webAppVersion);
        return false;
      }
      return true;
    }

    // Helper function to update button state
    function updateButton(): void {
      const btn_params = buttonParams();
      const btn_state = buttonState(btn_params);
      if (curButtonState === btn_state) {
        return;
      }
      curButtonState = btn_state;
      postEvent('web_app_setup_back_button', btn_params);
    }

    // Helper function to set button parameters
    function setParams(params: { is_visible?: boolean }): BackButtonInterface {
      if (!buttonCheckVersion()) {
        return backButton;
      }
      if (typeof params.is_visible !== 'undefined') {
        isVisible = !!params.is_visible;
      }
      updateButton();
      return backButton;
    }

    // Public methods
    backButton.onClick = function (callback: WebViewEventCallback): BackButtonInterface {
      if (buttonCheckVersion()) {
        onWebViewEvent('backButtonClicked', callback);
      }
      return backButton;
    };

    backButton.offClick = function (callback: WebViewEventCallback): BackButtonInterface {
      if (buttonCheckVersion()) {
        offWebViewEvent('backButtonClicked', callback);
      }
      return backButton;
    };

    backButton.show = function (): BackButtonInterface {
      return setParams({ is_visible: true });
    };

    backButton.hide = function (): BackButtonInterface {
      return setParams({ is_visible: false });
    };

    return backButton;
  })();

  // Create WebView object with only required methods
  const telegramWebView: TelegramWebView = {
    // Core data fields
    initParams,
    initData: initDataString,
    platform: initParams.tgWebAppPlatform || 'unknown',

    // Required methods
    expand: function () {
      postEvent('web_app_expand');
    },

    enableClosingConfirmation: function () {
      postEvent('web_app_setup_closing_behavior', { need_confirmation: true });
    },

    disableVerticalSwipes: function () {
      postEvent('web_app_setup_swipe_behavior', { allow_vertical_swipe: false });
    },

    requestFullscreen: function () {
      if (!versionAtLeast('8.0')) {
        console.error('[Telegram.WebApp] Method requestFullscreen is not supported in version ' + webAppVersion);
        throw Error('WebAppMethodUnsupported');
      }
      postEvent('web_app_request_fullscreen');
    },

    exitFullscreen: function () {
      if (!versionAtLeast('8.0')) {
        console.error('[Telegram.WebApp] Method exitFullscreen is not supported in version ' + webAppVersion);
        throw Error('WebAppMethodUnsupported');
      }
      postEvent('web_app_exit_fullscreen');
    },

    // 请求设备安全区，客户端异步回 safe_area_changed
    requestSafeArea: function () {
      if (!versionAtLeast('8.0')) {
        console.error('[Telegram.WebApp] Method requestSafeArea is not supported in version ' + webAppVersion);
        return;
      }
      postEvent('web_app_request_safe_area');
    },

    // 请求内容安全区（避开 Telegram 自己的悬浮 UI），客户端异步回 content_safe_area_changed
    requestContentSafeArea: function () {
      if (!versionAtLeast('8.0')) {
        console.error('[Telegram.WebApp] Method requestContentSafeArea is not supported in version ' + webAppVersion);
        return;
      }
      postEvent('web_app_request_content_safe_area');
    },

    isFullscreen: () => isFullscreenState,
    safeAreaInset: () => ({ ...safeAreaState }),
    contentSafeAreaInset: () => ({ ...contentSafeAreaState }),
    isVersionAtLeast: (ver: string) => versionAtLeast(ver),
    onFullscreenChanged: (callback) => onWebViewEvent('fullscreenChanged', dataOnly(callback)),
    offFullscreenChanged: (callback) => offWebViewEvent('fullscreenChanged', dataOnly(callback)),
    onFullscreenFailed: (callback) => onWebViewEvent('fullscreenFailed', dataOnly(callback)),
    offFullscreenFailed: (callback) => offWebViewEvent('fullscreenFailed', dataOnly(callback)),

    // Add BackButton to the WebView object
    BackButton,
    setBackgroundColor: function (color: string) {
      postEvent('web_app_set_background_color', { color: color });
    },
    downloadFile: function (params) {
      if (!versionAtLeast('8.0')) {
        window.open(params.url, '_blank');
        console.error('[Telegram.WebApp] Method downloadFile is not supported in version ' + webAppVersion);
        // throw Error('WebAppMethodUnsupported');
      }
      if (webAppDownloadFileRequested) {
        console.error('[Telegram.WebApp] Popup is already opened');
        throw Error('WebAppDownloadFilePopupOpened');
      }
      var a: any = document.createElement('A');

      var dl_params: any = {};
      if (!params || !params.url || !params.url.length) {
        console.error('[Telegram.WebApp] Url is required');
        throw Error('WebAppDownloadFileParamInvalid');
      }
      a.href = params.url;
      if (a.protocol != 'https:') {
        console.error('[Telegram.WebApp] Url protocol is not supported', params.url);
        throw Error('WebAppDownloadFileParamInvalid');
      }
      dl_params.url = a.href;

      if (!params || !params.file_name || !params.file_name.length) {
        console.error('[Telegram.WebApp] File name is required');
        throw Error('WebAppDownloadFileParamInvalid');
      }
      dl_params.file_name = params.file_name;

      postEvent('web_app_request_file_download', dl_params);
    },
    openLink: (url: string, options: any) => {
      var a: any = document.createElement('A');
      a.href = url;
      if (a.protocol != 'http:' &&
        a.protocol != 'https:') {
        console.error('[Telegram.WebApp] Url protocol is not supported', url);
        throw Error('WebAppTgUrlInvalid');
      }
      var url: string = a.href;
      options = options || {};
      if (versionAtLeast('6.1')) {
        var req_params: any = { url: url };
        if (versionAtLeast('6.4') && options.try_instant_view) {
          req_params.try_instant_view = true;
        }
        if (versionAtLeast('7.6') && options.try_browser) {
          req_params.try_browser = options.try_browser;
        }
        postEvent('web_app_open_link', req_params);
      } else {
        window.open(url, '_blank');
      }
    },
    openTelegramLink: (url: string, options: any) => {
      var a: any = document.createElement('A');
      a.href = url;
      if (a.protocol != 'http:' &&
        a.protocol != 'https:') {
        console.error('[Telegram.WebApp] Url protocol is not supported', url);
        throw Error('WebAppTgUrlInvalid');
      }
      if (a.hostname != 't.me') {
        console.error('[Telegram.WebApp] Url host is not supported', url);
        throw Error('WebAppTgUrlInvalid');
      }
      var path_full = a.pathname + a.search;
      options = options || {};
      if (isIframe || versionAtLeast('6.1')) {
        var req_params: any = { path_full: path_full };
        if (options.force_request) {
          req_params.force_request = true;
        }
        postEvent('web_app_open_tg_link', req_params);
      } else {
        window.open(url, '_blank');
      }
    },
    // Bot API 6.4+。官方是 callback 形式，这里包成 Promise；
    // 不支持/无响应时 resolve(null)，由调用方回退到 navigator.clipboard
    readTextFromClipboard: () => new Promise<string | null>((resolve) => {
      if (!versionAtLeast('6.4')) {
        console.error('[Telegram.WebApp] Method readTextFromClipboard is not supported in version ' + webAppVersion);
        resolve(null);
        return;
      }
      const req_id = generateCallbackId(16);
      let done = false;
      const finish = (text: string | null) => {
        if (done) return;
        done = true;
        delete webAppCallbacks[req_id];
        resolve(text);
      };
      webAppCallbacks[req_id] = { callback: finish };
      // Telegram 在无权限/用户拒绝时可能不回事件，兜底超时避免调用方一直挂起
      setTimeout(() => finish(null), 3000);
      postEvent('web_app_read_text_from_clipboard', { req_id });
    }),
    requestWriteAccess: () => new Promise<WriteAccessStatus | null>((resolve) => {
      if (!versionAtLeast('6.9')) {
        console.error('[Telegram.WebApp] Method requestWriteAccess is not supported in version ' + webAppVersion);
        resolve('unsupported');
        return;
      }
      if (pendingWriteAccess) {
        console.error('[Telegram.WebApp] Popup is already opened');
        resolve('unsupported');
        return;
      }
      let done = false;
      const finish = (status: WriteAccessStatus | null) => {
        if (done) return;
        done = true;
        if (pendingWriteAccess === finish) pendingWriteAccess = null;
        resolve(status);
      };
      pendingWriteAccess = finish;
      // 原生弹窗等用户操作，给足时间；客户端异常不回事件时兜底
      setTimeout(() => finish(null), 60000);
      postEvent('web_app_request_write_access');
    })
  };
  onEvent('write_access_requested', onWriteAccessRequested);
  onEvent('file_download_requested', onFileDownloadRequested);
  onEvent('clipboard_text_received', onClipboardTextReceived);
  onEvent('safe_area_changed', onSafeAreaChanged);
  onEvent('content_safe_area_changed', onContentSafeAreaChanged);
  onEvent('fullscreen_changed', onFullscreenChanged);
  onEvent('fullscreen_failed', onFullscreenFailed);

  return telegramWebView;
};

// Initialize and export the WebView instance
const TelegramWebView = createTelegramWebView();
export default TelegramWebView;